import type { CSSProperties } from 'react';

/**
 * Display props for user-authored text on the Data Privacy screen (PSTUDIO-4123).
 *
 * Kept as a pure module because the page itself cannot be rendered by a unit test
 * (it imports Lit design-system elements, which crash the node/jsdom
 * environment), so the values that decide the layout are pinned here instead.
 * What these values DO to the rendered card is a browser fact this suite cannot
 * observe; it only pins the inputs.
 */

/** Lines a rule name may occupy before it is cut with an ellipsis. */
const RULE_NAME_MAX_LINES = 2;

type RuleNameDisplayProps = {
  /** `ps-text-body` prop: clamps the text to this many lines. */
  truncateLines: number;
  style: CSSProperties;
  /** Native tooltip carrying the full text the clamp may have cut. */
  title: string;
};

/**
 * Props for the text node that shows a rule name next to its row actions.
 *
 * `truncateLines` clamps lines that exist; a name with no spaces is ONE line to
 * the browser, so `overflowWrap: 'anywhere'` is what gives the clamp something to
 * cut. `minWidth: 0` lets the flex item shrink below its content width, which is
 * what keeps the action buttons on screen. `title` restores the part the clamp
 * hides.
 */
export const getRuleNameDisplayProps = (
  name: string
): RuleNameDisplayProps => ({
  truncateLines: RULE_NAME_MAX_LINES,
  style: { overflowWrap: 'anywhere', minWidth: 0 },
  title: name,
});

type PatternDisplayProps = {
  dir: 'ltr';
  style: CSSProperties;
};

/**
 * Props for any node that shows or edits a masking pattern.
 *
 * A pattern is code, so it is pinned to left-to-right whatever the page
 * direction: under `dir="rtl"` (ar) the bidi algorithm reorders weak characters
 * such as `_` and `.` around the letters, and a stored pattern read back mirrored.
 * `unicodeBidi: 'isolate'` additionally stops the pattern from reordering the
 * text next to it. This pins the inputs only; whether the design-system `ps-input`
 * lets the host's `direction` reach its inner field is a browser fact that no
 * unit test here observes.
 */
export const getPatternDisplayProps = (): PatternDisplayProps => ({
  dir: 'ltr',
  style: { unicodeBidi: 'isolate' },
});
